import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from './tasksApi.js';
import { useUIStore } from '../../store/ui.store.js';

export const TASKS_KEY = (listId?: string) => ['tasks', listId ?? 'all'] as const;
export const SUBTASKS_KEY = (parentTaskId: string) => ['tasks', 'subtasks', parentTaskId] as const;

export function useTasks(listId?: string) {
  return useQuery({
    queryKey: TASKS_KEY(listId),
    queryFn: () => api.fetchTasks(listId ? { listId } : undefined),
    staleTime: 30_000,
  });
}

export function useSubtasks(parentTaskId: string, enabled = true) {
  return useQuery({
    queryKey: SUBTASKS_KEY(parentTaskId),
    queryFn: () => api.fetchTasks({ parentTaskId }),
    staleTime: 30_000,
    enabled,
  });
}

export function useTask(taskId: string) {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => api.fetchTask(taskId),
    staleTime: 30_000,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: api.createTask,
    onSuccess: (task) => {
      if (task.parentTaskId) {
        qc.invalidateQueries({ queryKey: SUBTASKS_KEY(task.parentTaskId) });
      } else {
        qc.invalidateQueries({ queryKey: TASKS_KEY(task.listId) });
        qc.invalidateQueries({ queryKey: TASKS_KEY() });
      }
      addToast(task.parentTaskId ? 'Subtask created' : 'Task created', 'success');
    },
    onError: () => addToast('Failed to create task', 'error'),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: api.UpdateTaskPayload }) =>
      api.updateTask(taskId, payload),
    onSuccess: (task) => {
      qc.setQueryData(['task', task.taskId], task);
      if (task.parentTaskId) {
        qc.invalidateQueries({ queryKey: SUBTASKS_KEY(task.parentTaskId) });
      } else {
        qc.invalidateQueries({ queryKey: TASKS_KEY(task.listId) });
        qc.invalidateQueries({ queryKey: TASKS_KEY() });
      }
    },
    onError: () => addToast('Failed to update task', 'error'),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ taskId }: { taskId: string; listId?: string; parentTaskId?: string }) =>
      api.deleteTask(taskId),
    onSuccess: (_data, { taskId, listId, parentTaskId }) => {
      qc.removeQueries({ queryKey: ['task', taskId] });
      qc.removeQueries({ queryKey: SUBTASKS_KEY(taskId) });
      if (parentTaskId) {
        qc.invalidateQueries({ queryKey: SUBTASKS_KEY(parentTaskId) });
      } else {
        // TASKS_KEY is cached per-list — invalidating only the "all" key
        // (like this used to) left the currently-viewed list's own cache
        // entry stale, so a deleted task stayed visible there until a
        // full refresh. Match useCreateTask/useUpdateTask and invalidate
        // both.
        qc.invalidateQueries({ queryKey: TASKS_KEY(listId) });
        qc.invalidateQueries({ queryKey: TASKS_KEY() });
      }
      addToast('Task deleted', 'success');
    },
    onError: () => addToast('Failed to delete task', 'error'),
  });
}
